import { ImageDescriptor, engine } from '@play-co/timestep-core';
import { ImageView, View, animate } from '@play-co/timestep-core/ui';
import TutorialHand from 'src/game/components/shared/TutorialHand';
import { captureGenericError } from 'src/lib/sentry';
import {
  getApplication,
  getScreenDimensions,
  getScreenExtraVerticalSpace,
} from 'src/lib/utils';
import {
  TutorialHandServiceShowArgs,
  getHandTarget,
} from './TutorialHandService';
import { ViewPosition } from '@play-co/timestep-core/lib/ui/View';

/**
 * This class is experimental, meant to be an alternative to TutorialHandService.
 * It uses a different approach to highlight the target view, which is to render it to an image on top of the overlay.
 * This only works fine with static views, like buttons. Movieclips like buruburu should be reparented instead.
 */
export class TutorialHandServiceAlt {
  private parent: View;
  private fill: View;
  private targetContainer: View;
  private clickTarget: View;
  private hand: TutorialHand;
  private curentTarget?: View;
  private showing = false;
  private renderedImage: ImageView;
  private cachedImages: Record<string, ImageDescriptor> = {};
  private targetOriginalSuperview?: View;
  private targetOriginalPosition?: ViewPosition;
  private shouldRenderTarget = false;
  private autoHide = true;

  constructor() {
    // Create parent view.
    let superview = new View();

    // Ignore errors if we cannot get the root view.
    try {
      superview = getApplication().getRootView();
    } catch (e) {
      console.error(e);
      captureGenericError('Cannot get root view for tutorial hand service', e);
    }

    this.parent = new View({
      superview,
      visible: false,
    });

    // Create fill.
    this.fill = new View({
      superview: this.parent,
      backgroundColor: '#784E99CC',
      opacity: 0,
    });

    this.renderedImage = new ImageView({
      superview: this.fill,
    });

    this.targetContainer = new View({
      superview: this.parent,
      visible: false,
    });

    // Create click target.
    this.clickTarget = new View({
      superview: this.parent,
      canHandleEvents: false, // Disable by default, will be enabled on show.
    });

    // Pass events through to target.
    this.clickTarget.onInputStart = (...args) => {
      this.curentTarget?.onInputStart?.bind(this.curentTarget)(...args);
    };
    this.clickTarget.onInputSelect = (...args) => {
      const target = this.curentTarget as { pressed?: boolean };
      // Handles the case where the target is a button and it is not pressed.
      if (
        target &&
        target.pressed !== null &&
        target.pressed !== undefined &&
        !target.pressed
      ) {
        return;
      }
      this.disableSelect();
      if (this.autoHide) void this.hide();
      this.curentTarget?.onInputSelect?.bind(this.curentTarget)(...args);
    };
    this.clickTarget.onInputOut = (...args) => {
      this.curentTarget?.onInputOut?.bind(this.curentTarget)(...args);
    };
    this.clickTarget.onInputOver = (...args) => {
      this.curentTarget?.onInputOver?.bind(this.curentTarget)(...args);
    };
    this.clickTarget.onInputMove = (...args) => {
      this.curentTarget?.onInputMove?.bind(this.curentTarget)(...args);
    };

    // Create hand.
    this.hand = new TutorialHand({
      superview: this.clickTarget,
    });

    // Tick to updat.onInputSelect
    engine.on('Tick', this.tick.bind(this));
  }

  public async show(args: TutorialHandServiceShowArgs) {
    const { tag, x, y } = args;
    const zIndex = args.zIndex ?? 1000;
    const reversed = args.reversed ?? false;
    this.autoHide = args.autoHide ?? true;
    if (!getHandTarget(tag)) {
      // Do not throw error in production, carry on.
      if (process.env.IS_DEVELOPMENT) {
        throw new Error(`Hand target ${tag} not found`);
      }

      captureGenericError(
        'Cannot display tutorial hand',
        new Error(`Hand target ${tag} not found`),
      );

      return;
    }

    this.showing = true;
    this.curentTarget = getHandTarget(tag);

    // Only render targets that are not buruburu.
    this.shouldRenderTarget = tag !== 'buruburu';
    this.renderedImage.updateOpts({ visible: this.shouldRenderTarget });

    this.parent.updateOpts({
      zIndex,
      visible: true,
    });
    // Bring target to the front, if not rendered.
    this.bringTargetToFront();
    animate(this.fill).then({ opacity: 1 }, 500);

    // Disable select while playing animation.
    this.disableSelect();

    // Play show animation.
    await this.hand.fadeIn(x, y, reversed);

    // Re-enable select after animation.
    this.enableSelect();
  }

  public enableSelect() {
    this.clickTarget.updateOpts({ canHandleEvents: true });
  }

  public disableSelect() {
    this.clickTarget.updateOpts({ canHandleEvents: false });
  }

  public async hide() {
    this.disableSelect();
    this.showing = false;
    this.hand.fadeOut();
    // Send target back to its original parent, if not rendered.
    this.sendTargetBack();
    await animate(this.fill).then({ opacity: 0 }, 300).toPromise();
    this.parent.hide();
    delete this.curentTarget;
  }

  private getScrenOffsetY() {
    return getScreenExtraVerticalSpace() / 2;
  }

  /**
   * Allow clearing current target without removing the blocking overlay which could be useful
   * for a sequence of targets where we want the screen to be cleared but not interactive.
   * Ex.: show -> aks for action -> clear target -> wait something -> show again
   */
  public async clearScreen() {
    this.disableSelect();
    // Bring target back to its owriginal parent, if not rendered.
    this.sendTargetBack();
    this.hand.fadeOut();
    await animate(this.fill).then({ opacity: 0 }, 300).toPromise();
  }

  private tick() {
    if (!this.showing) return;

    const root = getApplication().getRootView();
    const width = root.style.width;
    const height = getScreenDimensions().height;

    this.parent.updateOpts({
      width: width,
      height: height,
      y: -this.getScrenOffsetY(),
      visible: true,
    });

    this.fill.updateOpts({
      width: width,
      height: height,
    });

    this.targetContainer.updateOpts({
      width: width,
      height: height,
    });

    if (this.curentTarget?.getSuperviews().includes(root)) {
      const position = this.curentTarget.getPosition(root);
      position.y += this.getScrenOffsetY();
      this.clickTarget.updateOpts(position);
      // Render the target in front, if needed
      this.renderTarget();
    }
  }

  private bringTargetToFront() {
    // Skip if there is no target or if we should be rendered.
    if (!this.curentTarget || this.shouldRenderTarget) return;
    this.targetContainer.show();

    const root = getApplication().getRootView();
    this.targetOriginalSuperview =
      this.curentTarget.getSuperview() ?? undefined;
    this.targetOriginalPosition = this.curentTarget.getPosition(
      this.targetOriginalSuperview,
    );

    const position = this.curentTarget.getPosition(root);
    position.y += this.getScrenOffsetY();
    this.curentTarget.updateOpts({
      superview: this.targetContainer,
      x: position.x,
      y: position.y,
      width: position.width,
      height: position.height,
      scale: position.scale,
      anchorX: position.anchorX,
      anchorY: position.anchorY,
    });
  }

  private sendTargetBack() {
    // Skip if there is no target or if we should be rendered.
    if (!this.curentTarget || this.shouldRenderTarget) return;
    this.targetContainer.hide();

    if (this.targetOriginalSuperview) {
      this.curentTarget.updateOpts({
        superview: this.targetOriginalSuperview,
      });
      this.targetOriginalSuperview = undefined;
    }

    if (this.targetOriginalPosition) {
      this.curentTarget.updateOpts({
        x: this.targetOriginalPosition.x,
        y: this.targetOriginalPosition.y,
        width: this.targetOriginalPosition.width,
        height: this.targetOriginalPosition.height,
        scale: this.targetOriginalPosition.scale,
        anchorX: this.targetOriginalPosition.anchorX,
        anchorY: this.targetOriginalPosition.anchorY,
      });
      this.targetOriginalPosition = undefined;
    }
  }

  private renderTarget() {
    // Skip if target should not be rendered.
    if (!this.shouldRenderTarget) return;

    // Skip if there is no target or if the overlay is not visible.
    if (!this.curentTarget || !this.fill.style.opacity) return;

    const root = getApplication().getRootView();

    // Skip if target is not in root.
    if (!this.curentTarget.getSuperviews().includes(root)) return;

    const renderWidth = this.curentTarget.style.width;
    const renderHeight = this.curentTarget.style.height;

    const uid = String(this.curentTarget.uid);
    const image =
      this.cachedImages[uid] ||
      this.curentTarget.style.renderToImage(uid, {
        width: renderWidth,
        height: renderHeight,
        offsetX: 0,
        offsetY: 0,
      });
    this.cachedImages[uid] = image;
    this.renderedImage.updateOpts({ image });

    const position = this.curentTarget.getPosition(root);
    position.y += this.getScrenOffsetY();
    this.renderedImage.updateOpts({
      x: position.x,
      y: position.y,
      width: position.width,
      height: position.height,
      scale: position.scale,
      anchorX: position.anchorX,
      anchorY: position.anchorY,
    });
  }
}
